import { useCallback, useEffect, useState } from 'react';
import {
  getEmargements,
  getPlanification,
  apercuEnvoiDocument,
  apercuLiensEmargement,
  envoyerDocumentSession,
  envoyerLiensEmargement,
  modifierFeuille,
  regenererPdfDocument,
  signerEmargement,
  supprimerEmargement,
  urlPdfDocument,
} from '../services/sessionService';
import ApercuEmail from './ApercuEmail';
import { formatDate, formatDateTime } from './devisUtils';
import PanneauApercu from './PanneauApercu';
import SignaturePad from './SignaturePad';
import '../assets/styles/Listes.css';
import './Signature.css';
import { toast } from '../components/Toasts';
import { IconeTelecharger } from '../components/IconesDocument';

/** aaaa-mm-jj : la forme sous laquelle le serveur compare les journées. */
const jourCle = (valeur) => String(valeur || '').slice(0, 10);

/** « 16/09 » — l'en-tête d'une colonne d'émargement est étroit. */
function jourCourt(valeur) {
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** « 16 sept. 2026 · 17 sept. 2026 » — l'ordre vient du serveur. */
const joursTexte = (jours = []) => jours.map((j) => formatDate(j)).join(' · ');

/** Les deux cases d'une journée, dans l'ordre des colonnes du PDF. */
const DEMI_JOURNEES = [
  { cle: 'matin', libelle: 'Matin' },
  { cle: 'apres_midi', libelle: 'Après-midi' },
];

/**
 * La case que l'envoi propose d'abord : la journée du jour si la feuille en
 * a une (sinon la prochaine, sinon la dernière), et l'après-midi à partir de
 * 13 h ce jour-là — l'heure de l'envoi automatique. On peut toujours changer
 * avant d'envoyer.
 */
function caseParDefaut(jours = []) {
  const tries = jours.map(jourCle).sort();
  const maintenant = new Date();
  const aujourdhui = jourCle(
    new Date(maintenant.getTime() - maintenant.getTimezoneOffset() * 60000).toISOString()
  );
  const jour = tries.includes(aujourdhui)
    ? aujourdhui
    : tries.find((j) => j > aujourdhui) || tries[tries.length - 1] || '';
  const demi = jour === aujourdhui && maintenant.getHours() >= 13 ? 'apres_midi' : 'matin';
  return { jour, demi, toutes: false };
}

const nomComplet = (p) => [p.prenom, p.nom].filter(Boolean).join(' ') || '—';

const feuilleDe = (session) =>
  (session.documents || []).find((d) => d.type === 'feuille_presence');

/**
 * Cases à signer et cases signées d'une feuille, telles que la liste du
 * contrat les affiche. Une case par personne et par journée : les
 * participants d'un côté, le formateur (participant fictif 0) de l'autre —
 * le menu Formateurs ne compte que les siennes, les pages clients que celles
 * des participants.
 */
function comptesFeuille(session, doc, formateurSeul) {
  const jours = (session.jours || []).length;
  if (formateurSeul) {
    return {
      cases: session.formateur_id ? jours * 2 : 0,
      signees: Number(doc.nb_emargements_formateur) || 0,
    };
  }
  return {
    cases: (Number(doc.nb_participants) || 0) * jours * 2,
    signees: Number(doc.nb_emargements) || 0,
  };
}

/** Même règle sur la feuille ouverte, à partir de ses émargements détaillés. */
function comptesOuverte(feuille, formateurSeul) {
  if (!feuille) return { cases: 0, signees: 0 };
  const jours = (feuille.jours || []).length;
  const emargements = feuille.emargements || [];
  if (formateurSeul) {
    return {
      cases: feuille.formateur ? jours * 2 : 0,
      signees: emargements.filter((m) => Number(m.participant_id) === 0).length,
    };
  }
  return {
    cases: (feuille.participants || []).length * jours * 2,
    signees: emargements.filter((m) => Number(m.participant_id) !== 0).length,
  };
}

/**
 * Feuilles de présence d'un contrat, ouvertes en cliquant une ligne de la
 * liste.
 *
 * Deux temps : toutes les feuilles du dossier, puis celle qu'on choisit,
 * rendue comme le PDF — une ligne par participant, une colonne par journée.
 * Chaque case s'émarge à l'ardoise ; le tracé, le nom du signataire et
 * l'horodatage tiennent dans la case, et le PDF les reprend à sa prochaine
 * génération.
 */
/**
 * `lectureSeule` : depuis le menu Formateurs, la feuille se lit et s'émarge,
 * mais ne se corrige pas — la planification reste le lieu des modifications.
 * `ouvrirDirectement` : depuis le détail d'une session, on arrive sur la
 * feuille visée, sans repasser par la liste des feuilles du contrat.
 */
function FeuillesPresence({
  contrat,
  documentId,
  onClose,
  modeInitial = 'apercu',
  lectureSeule = false,
  ouvrirDirectement = false,
}) {
  const [sessions, setSessions] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  // Feuille ouverte : son identifiant, son contenu, et la case en cours de
  // signature dans son tableau.
  const [ouverte, setOuverte] = useState(null);
  const [feuille, setFeuille] = useState(null);
  const [mode, setMode] = useState(modeInitial);
  const [caseSignee, setCaseSignee] = useState(null);
  // Correction de la feuille : ouverte à la demande, et seulement tant que
  // la feuille n'est pas partie.
  const [correction, setCorrection] = useState(null);
  const [enregistrement2, setEnregistrement2] = useState(false);
  const [erreurCorrection, setErreurCorrection] = useState(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  // Envoi du lien de signature : la case choisie (journée, demi-journée),
  // ouverte à la demande depuis la feuille — jamais envoyée sans choix.
  const [envoiLien, setEnvoiLien] = useState(null);
  const [envoiLienEnCours, setEnvoiLienEnCours] = useState(false);
  const [erreurEnvoiLien, setErreurEnvoiLien] = useState(null);
  // Une signature efface le PDF rendu : le cadre doit redemander le nouveau,
  // pas resservir celui que le navigateur garde en cache.
  const [version, setVersion] = useState(0);
  const [enregistrement, setEnregistrement] = useState(false);
  const [regeneration, setRegeneration] = useState(false);

  /** Refait le PDF de la feuille à partir de la session à jour (participants, dates, signatures). */
  async function regenererPdf() {
    setRegeneration(true);
    try {
      const res = await regenererPdfDocument(ouverte);
      setVersion((v) => v + 1);
      toast.succes(res.message || 'PDF de la feuille refait.');
    } catch (err) {
      toast.erreur(err.message || 'Régénération impossible.');
    } finally {
      setRegeneration(false);
    }
  }

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await getPlanification(contrat.id);
      const avecFeuille = (res.data.sessions || []).filter(feuilleDe);
      setSessions(avecFeuille);

      // Une seule feuille au dossier : l'étape de choix n'apprendrait rien.
      // Et le bouton « Envoyer » d'une liste vise une feuille précise : on
      // l'ouvre directement sur le choix de la case.
      const visee = avecFeuille.find((s) => feuilleDe(s).id === documentId);
      if (visee && (modeInitial === 'envoi' || ouvrirDirectement)) setOuverte(documentId);
      else if (avecFeuille.length === 1) setOuverte(feuilleDe(avecFeuille[0]).id);
    } catch (err) {
      setErreur(err.message || 'Chargement des feuilles impossible.');
    } finally {
      setChargement(false);
    }
  }, [contrat.id, documentId, modeInitial, ouvrirDirectement]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Arrivé depuis le bouton « Envoyer » d'une liste : la feuille s'ouvre
  // directement sur le choix de la case, une fois son contenu chargé.
  useEffect(() => {
    if (mode === 'envoi' && feuille && !envoiLien) {
      setEnvoiLien(caseParDefaut(feuille.jours));
    }
  }, [mode, feuille, envoiLien]);

  // Le contenu de la feuille suit celle qui est ouverte.
  useEffect(() => {
    if (!ouverte) {
      setFeuille(null);
      return undefined;
    }
    let vivant = true;
    getEmargements(ouverte)
      .then((res) => {
        if (vivant) setFeuille(res.data);
      })
      .catch((err) => {
        if (vivant) setErreur(err.message || 'Chargement de la feuille impossible.');
      });
    return () => {
      vivant = false;
    };
  }, [ouverte]);

  // Deux cases par journée (matin, après-midi), comme sur la feuille papier.
  const signature = (participantId, jour, demi = 'matin') =>
    (feuille?.emargements || []).find(
      (m) =>
        Number(m.participant_id) === Number(participantId) &&
        jourCle(m.jour) === jourCle(jour) &&
        (m.demi_journee || 'matin') === demi
    );

  async function poser(imageDataUrl) {
    setEnregistrement(true);
    setErreur(null);
    try {
      const res = await signerEmargement(ouverte, {
        participantId: caseSignee.participant.id,
        jour: jourCle(caseSignee.jour),
        demiJournee: caseSignee.demi,
        image: imageDataUrl,
      });
      setFeuille(res.data);
      setCaseSignee(null);
      setVersion((v) => v + 1);
      // La signature posée, retour à l'aperçu : le PDF vient d'être refait
      // côté serveur, le cadre le recharge et montre le tracé en place.
      setMode('apercu');
    } catch (err) {
      setErreur(err.message || 'Signature impossible.');
    } finally {
      setEnregistrement(false);
    }
  }

  async function effacer(participantId, jour, demi) {
    setErreur(null);
    try {
      const res = await supprimerEmargement(ouverte, participantId, jourCle(jour), demi);
      setFeuille(res.data);
      setVersion((v) => v + 1);
    } catch (err) {
      setErreur(err.message || 'Suppression impossible.');
    }
  }

  const { cases, signees } = comptesOuverte(feuille, lectureSeule);

  // Toutes les feuilles du contrat additionnées : deux feuilles de deux
  // journées font quatre cases à signer, pour le formateur comme pour chaque
  // participant.
  const total = sessions.reduce(
    (acc, s) => {
      const c = comptesFeuille(s, feuilleDe(s), lectureSeule);
      return { cases: acc.cases + c.cases, signees: acc.signees + c.signees };
    },
    { cases: 0, signees: 0 }
  );

  /**
   * Envoie la feuille émargée au contact du contrat.
   *
   * On recharge après coup : le serveur pose la date d'envoi, et c'est elle
   * qui fait passer le bouton à « Renvoyer » et verrouille la correction.
   */
  // Les aperçus d'email avant envoi : feuille au client, lien d'émargement.
  const [apercuFeuille, setApercuFeuille] = useState(false);
  const [apercuLien, setApercuLien] = useState(null);

  /** Ouvre l'aperçu de l'email ; rien ne part avant le clic dans la modale. */
  function envoyerAuClient() {
    if (!ouverte) return;
    setErreur(null);
    setApercuFeuille(true);
  }

  /** L'envoi réel de la feuille, confirmé depuis l'aperçu. */
  async function envoyerFeuilleApresApercu({ sujet, texte }) {
    setEnvoiEnCours(true);
    try {
      const res = await envoyerDocumentSession(ouverte, { sujet, texte });
      setErreur(null);
      await charger();
      // Le serveur vient de poser la date d'envoi : la feuille ouverte la
      // relit. Sans cela le bouton restait « Envoyer au client » et la
      // correction ouverte, alors que le serveur la refuse désormais.
      try {
        const maj = await getEmargements(ouverte);
        setFeuille(maj.data);
      } catch {
        /* l'email est parti : le bouton se mettra à jour à la réouverture */
      }
      toast.succes(res.message || 'Feuille de présence envoyée au client.');
    } finally {
      setEnvoiEnCours(false);
    }
  }

  /** La séance dont la feuille ouverte est le document : c'est elle qui reçoit l'envoi. */
  const sessionOuverte = sessions.find((s) => feuilleDe(s)?.id === ouverte) || null;

  /**
   * Envoie le lien de signature pour la case choisie : à chaque participant
   * depuis les pages clients, au seul formateur depuis le menu Formateurs.
   * Le même email que l'envoi automatique de cette demi-journée.
   */
  function envoyerLien(e) {
    e.preventDefault();
    if (!sessionOuverte || (!envoiLien?.toutes && !envoiLien?.jour)) return;
    setErreurEnvoiLien(null);
    // L'aperçu d'abord : l'email ne part qu'à sa confirmation.
    setApercuLien({ jour: envoiLien.jour, demi: envoiLien.demi, toutes: envoiLien.toutes });
  }

  /** L'envoi réel des liens, confirmé depuis l'aperçu. */
  async function envoyerLienApresApercu({ sujet, texte }) {
    setEnvoiLienEnCours(true);
    try {
      const res = await envoyerLiensEmargement(
        sessionOuverte.id,
        lectureSeule ? 'formateur' : 'participants',
        { jour: apercuLien.jour, demiJournee: apercuLien.demi, toutes: apercuLien.toutes, sujet, texte }
      );
      setEnvoiLien(null);
      setMode('apercu');
      toast.succes(res.message || 'Lien d’émargement envoyé.');
    } finally {
      setEnvoiLienEnCours(false);
    }
  }

  const titre = feuille ? feuille.intitule : 'Feuilles de présence';
  const sousTitre = `${contrat.numero} · ${contrat.societe_nom}${
    feuille
      ? ` · ${joursTexte(feuille.jours)}`
      : ` · ${sessions.length} feuille${sessions.length > 1 ? 's' : ''}` +
        (total.cases ? ` · ${total.signees}/${total.cases} signée${total.cases > 1 ? 's' : ''}` : '')
  }`;

  const actions = (
    <>
      {/* Venu d'une liste qui montre déjà toutes les feuilles : pas de
          seconde liste ici, fermer ramène à la page. */}
      {ouverte && sessions.length > 1 && !ouvrirDirectement && (
        <button
          type="button"
          className="devis-btn devis-btn-sm"
          onClick={() => {
            setOuverte(null);
            setMode('apercu');
            setCaseSignee(null);
          }}
        >
          Toutes les feuilles
        </button>
      )}
      {ouverte && (
        <>
          <button
            type="button"
            className={`devis-btn devis-btn-sm${
              mode === 'emargement' ? '' : ' devis-btn-primary'
            }`}
            onClick={() => {
              setMode(mode === 'emargement' ? 'apercu' : 'emargement');
              setCaseSignee(null);
              setCorrection(null);
              setEnvoiLien(null);
            }}
          >
            {mode === 'emargement' ? 'Aperçu' : 'Émarger'}
          </button>
          {/* Plus de bouton « Envoyer le lien » ici : les liens d'émargement
              partent automatiquement (planificateur) ou depuis la page
              Feuille de présence ; le mode 'envoi' reste atteignable par là. */}
          {/* Une feuille partie ne se corrige plus : le bouton disparaît
              plutôt que d'ouvrir un formulaire que le serveur refusera. */}
          {feuille && !feuille.envoyeeLe && !lectureSeule && (
            <button
              type="button"
              className="devis-btn devis-btn-sm feuille-corriger"
              onClick={() => {
                setCorrection({
                  formateurs: feuille.formateur || '',
                  formateurEmail: feuille.formateurEmail || '',
                });
                setErreurCorrection(null);
                setCaseSignee(null);
                // En aperçu, le corps est occupé par le cadre du PDF : sans
                // ce basculement, le formulaire n'aurait nulle part où
                // s'afficher et le bouton semblerait sans effet.
                setMode('correction');
              }}
            >
              Corriger
            </button>
          )}
          {/* La feuille émargée est une pièce que le client réclame, et
              qu'un audit demande. Elle part à la demande et jamais seule :
              c'est à l'organisme de juger qu'elle est en état d'être remise. */}
          {feuille && !lectureSeule && (
            <button
              type="button"
              className={`devis-btn devis-btn-sm${
                feuille.envoyeeLe ? '' : ' devis-btn-primary'
              }`}
              onClick={envoyerAuClient}
              disabled={envoiEnCours}
              title={
                feuille.envoyeeLe
                  ? `Déjà envoyée le ${formatDate(feuille.envoyeeLe)} — renvoyer`
                  : 'Envoyer la feuille émargée au client'
              }
            >
              {envoiEnCours
                ? 'Envoi…'
                : feuille.envoyeeLe
                  ? 'Renvoyer au client'
                  : 'Envoyer au client'}
            </button>
          )}
          <button
            type="button"
            className="devis-btn devis-btn-sm"
            onClick={regenererPdf}
            disabled={regeneration}
            title="Refaire le PDF de la feuille à partir des données actuelles de la session"
          >
            {regeneration ? 'Régénération' : 'Régénérer le PDF'}
          </button>
          <button
            type="button"
            className="devis-btn devis-btn-sm devis-btn-fichier"
            onClick={() => window.open(urlPdfDocument(ouverte), '_blank')}
          >
            <IconeTelecharger />
            PDF
          </button>
        </>
      )}
    </>
  );

  /* Aperçu, sauf quand on émarge ou qu'il reste à choisir une feuille : le
     corps passe alors en `children` et remplace le cadre du PDF. */
  const corps =
    !ouverte || mode === 'emargement' || mode === 'correction' || mode === 'envoi' ? (
      <>
        <div className="devis-modal-body">
          {chargement && <div className="devis-drawer-empty">Chargement...</div>}
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          {!chargement && sessions.length === 0 && (
            <div className="devis-drawer-empty">
              Aucune feuille de présence générée sur ce contrat.
            </div>
          )}

          {!chargement &&
            !ouverte &&
            sessions.map((s) => {
              const doc = feuilleDe(s);
              return (
                <button
                  type="button"
                  key={s.id}
                  className={`feuille-carte${doc.id === documentId ? ' feuille-carte-venue' : ''}`}
                  onClick={() => setOuverte(doc.id)}
                >
                  <span className="feuille-carte-corps">
                    <span className="feuille-carte-titre">{s.intitule}</span>
                    <span className="feuille-carte-meta">
                      {joursTexte(s.jours)}
                      {s.groupe_libelle ? ` · ${s.groupe_libelle}` : ''}
                      {s.lieu ? ` · ${s.lieu}` : ''}
                    </span>
                  </span>
                  <span className="feuille-carte-etat">
                    {(() => {
                      const c = comptesFeuille(s, doc, lectureSeule);
                      return c.cases
                        ? `${c.signees}/${c.cases} signée${c.cases > 1 ? 's' : ''}`
                        : 'aucune case à signer';
                    })()}
                    <span className="feuille-carte-date">
                      {doc.nb_participants} participant{doc.nb_participants > 1 ? 's' : ''} ·
                      générée le {formatDate(doc.updated_at)}
                    </span>
                  </span>
                </button>
              );
            })}

          {ouverte && feuille && mode === 'envoi' && envoiLien && (
            <form className="feuille-correction" onSubmit={envoyerLien}>
              <div className="feuille-correction-titre">Envoyer le lien de signature</div>
              <p className="feuille-correction-aide">
                {lectureSeule
                  ? 'Le formateur reçoit son lien '
                  : 'Chaque participant avec une adresse reçoit son lien '}
                {envoiLien.toutes
                  ? 'sur la feuille complète : toutes les journées, matin et après-midi. '
                  : 'sur la feuille complète, avec la case choisie déjà prête à signer. (Envoi auto : matin au début de séance, après-midi à 13 h.)'}
              </p>

              {/* Plutôt qu'une seule case : le lien ouvre la ligne du signataire
                  avec toutes les dates et toutes les colonnes à signer, comme
                  l'onglet « Émarger » d'ici. */}
              <label className="feuille-envoi-toutes">
                <input
                  type="checkbox"
                  checked={Boolean(envoiLien.toutes)}
                  onChange={(e) => setEnvoiLien((c) => ({ ...c, toutes: e.target.checked }))}
                  disabled={envoiLienEnCours}
                />
                <span>
                  <strong>Envoyer toutes les journées et demi-journées</strong>
                  <span className="feuille-envoi-toutes-aide">
                    Le message parle de toute la formation et la feuille s’ouvre sans case
                    présélectionnée. Décochée, la case choisie ci-dessous est déjà prête à signer.
                  </span>
                </span>
              </label>

              <div className="feuille-correction-champs">
                <label className="devis-form-field">
                  <span>Journée</span>
                  <select
                    value={envoiLien.jour}
                    onChange={(e) => setEnvoiLien((c) => ({ ...c, jour: e.target.value }))}
                    disabled={envoiLienEnCours || envoiLien.toutes}
                  >
                    {(feuille.jours || []).map((j) => (
                      <option key={jourCle(j)} value={jourCle(j)}>
                        {formatDate(j)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="devis-form-field">
                  <span>Demi-journée</span>
                  <select
                    value={envoiLien.demi}
                    onChange={(e) => setEnvoiLien((c) => ({ ...c, demi: e.target.value }))}
                    disabled={envoiLienEnCours || envoiLien.toutes}
                  >
                    {DEMI_JOURNEES.map((d) => (
                      <option key={d.cle} value={d.cle}>
                        {d.libelle}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {erreurEnvoiLien && (
                <div className="devis-alert devis-alert-erreur">{erreurEnvoiLien}</div>
              )}

              <div className="feuille-correction-actions">
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => {
                    setEnvoiLien(null);
                    setMode('apercu');
                  }}
                  disabled={envoiLienEnCours}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="devis-btn devis-btn-sm devis-btn-primary"
                  disabled={envoiLienEnCours || (!envoiLien.toutes && !envoiLien.jour)}
                >
                  {envoiLienEnCours
                    ? 'Envoi…'
                    : lectureSeule
                      ? 'Envoyer au formateur'
                      : 'Envoyer aux participants'}
                </button>
              </div>
            </form>
          )}

          {ouverte && feuille && correction && (
            <form
              className="feuille-correction"
              onSubmit={async (e) => {
                e.preventDefault();
                setEnregistrement2(true);
                setErreurCorrection(null);
                try {
                  await modifierFeuille(ouverte, correction);
                  const res = await getEmargements(ouverte);
                  setFeuille(res.data);
                  setCorrection(null);
                  setMode('apercu');
                } catch (err) {
                  setErreurCorrection(err.message || 'Correction impossible.');
                } finally {
                  setEnregistrement2(false);
                }
              }}
            >
              <div className="feuille-correction-titre">Corriger la feuille</div>
              <p className="feuille-correction-aide">
                Dates et participants se corrigent dans la planification.
              </p>

              <div className="feuille-correction-champs">
                <label className="devis-form-field">
                  <span>Formateur / formatrice</span>
                  <input
                    value={correction.formateurs}
                    placeholder="Prénom NOM"
                    onChange={(e) =>
                      setCorrection((c) => ({ ...c, formateurs: e.target.value }))
                    }
                  />
                </label>
                <label className="devis-form-field">
                  <span>Son email (rappels et lien d&apos;émargement)</span>
                  <input
                    type="email"
                    value={correction.formateurEmail}
                    placeholder="prenom.nom@example.fr"
                    onChange={(e) =>
                      setCorrection((c) => ({ ...c, formateurEmail: e.target.value }))
                    }
                  />
                </label>
              </div>

              {erreurCorrection && (
                <div className="devis-alert devis-alert-erreur">{erreurCorrection}</div>
              )}

              <div className="feuille-correction-actions">
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => {
                    setCorrection(null);
                    setMode('apercu');
                  }}
                  disabled={enregistrement2}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="devis-btn devis-btn-sm devis-btn-primary"
                  disabled={enregistrement2}
                >
                  {enregistrement2 ? 'Enregistrement' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}

          {ouverte && feuille && mode !== 'correction' && mode !== 'envoi' && (
            <TableauEmargement
              feuille={feuille}
              signature={signature}
              onSigner={(participant, jour, demi) => setCaseSignee({ participant, jour, demi })}
              onEffacer={effacer}
            />
          )}

          {/* Ce que le formateur a écrit depuis son lien (migration 070) :
              le cadre « Notes du formateur » du PDF. Lu ici, écrit par lui. */}
          {ouverte && feuille && mode !== 'correction' && mode !== 'envoi' && feuille.notesFormateur && (
            <div className="devis-drawer-field" style={{ marginTop: 12 }}>
              <span className="devis-drawer-label">
                Notes du formateur
                {feuille.notesFormateurLe ? ` · ${formatDateTime(feuille.notesFormateurLe)}` : ''}
              </span>
              <p className="devis-drawer-message">{feuille.notesFormateur}</p>
            </div>
          )}
        </div>

        {caseSignee && (
          <div className="feuille-ardoise">
            <div className="feuille-ardoise-titre">
              {nomComplet(caseSignee.participant)} · {formatDate(caseSignee.jour)} ·{' '}
              {caseSignee.demi === 'apres_midi' ? 'après-midi' : 'matin'}
            </div>
            <SignaturePad onValider={poser} validation={enregistrement} />
            <button
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => setCaseSignee(null)}
              disabled={enregistrement}
            >
              Annuler
            </button>
          </div>
        )}

        {ouverte && feuille && !caseSignee && (
          <div className="devis-modal-footer feuille-pied">
            <span className="feuille-pied-note">
              {signees} case{signees > 1 ? 's' : ''} émargée{signees > 1 ? 's' : ''} sur {cases}
              {lectureSeule ? ' pour le formateur' : ' pour les participants'} · l’aperçu et le
              PDF les reprennent
            </span>
          </div>
        )}
      </>
    ) : null;

  return (
    <>
      <PanneauApercu
        titre={titre}
        sousTitre={sousTitre}
        url={ouverte ? `${urlPdfDocument(ouverte, { apercu: true })}&v=${version}` : null}
        actions={actions}
        onClose={onClose}
      >
        {corps}
      </PanneauApercu>

      {apercuFeuille && ouverte && (
        <ApercuEmail
          titre="Envoyer la feuille de présence"
          sousTitre={sousTitre}
          charger={() => apercuEnvoiDocument(ouverte)}
          onEnvoyer={envoyerFeuilleApresApercu}
          onClose={() => setApercuFeuille(false)}
        />
      )}

      {apercuLien && sessionOuverte && (
        <ApercuEmail
          titre="Envoyer le lien de signature"
          sousTitre={sousTitre}
          charger={() =>
            apercuLiensEmargement(sessionOuverte.id, lectureSeule ? 'formateur' : 'participants', {
              jour: apercuLien.jour,
              demiJournee: apercuLien.demi,
              toutes: apercuLien.toutes,
            })
          }
          onEnvoyer={envoyerLienApresApercu}
          onClose={() => setApercuLien(null)}
        />
      )}
    </>
  );
}

/**
 * Le tableau tel que le PDF le rend : une ligne par participant, une colonne
 * par journée. Une case vide se clique pour signer ; une case signée montre le
 * tracé, le nom et l'heure, et se rend au « × » si c'était une erreur.
 */
/**
 * Le formateur émarge sur la même feuille sans y avoir de fiche : il porte
 * le numéro 0 (migration 038). On le pose en dernière ligne, comme sur le
 * PDF, pour que le back-office puisse signer à sa place quand il n'a pas
 * reçu son lien.
 */
const ID_FORMATEUR = 0;

function TableauEmargement({ feuille, signature, onSigner, onEffacer }) {
  const { jours = [], participants = [] } = feuille;

  const lignes = [
    ...participants.map((p, i) => ({ ...p, rang: i + 1 })),
    ...(feuille.formateur
      ? [{ id: ID_FORMATEUR, nom: feuille.formateur, qualite: 'Formateur/trice', rang: null }]
      : []),
  ];

  if (!participants.length) {
    return (
      <div className="devis-drawer-empty">
        Aucun participant : ajoutez-les dans la planification, puis régénérez la feuille.
      </div>
    );
  }

  return (
    <div className="feuille-tableau-cadre">
      <table className="feuille-tableau">
        <thead>
          <tr>
            <th className="feuille-rang">N°</th>
            <th>NOM / PRÉNOM</th>
            <th>SOCIÉTÉ</th>
            {jours.map((j) => (
              <th key={jourCle(j)} className="feuille-colonne-signature" colSpan={2}>
                Signature
                <span>{jourCourt(j)}</span>
              </th>
            ))}
          </tr>
          <tr>
            <th className="feuille-rang" />
            <th />
            <th />
            {jours.flatMap((j) =>
              DEMI_JOURNEES.map((d) => (
                <th key={`${jourCle(j)}-${d.cle}`} className="feuille-colonne-signature">
                  {d.libelle}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {lignes.map((p) => (
            <tr key={p.id} className={p.rang === null ? 'feuille-ligne-formateur' : undefined}>
              <td className="feuille-rang">{p.rang ?? '—'}</td>
              <td>
                <div className="feuille-nom texte-borne">{nomComplet(p)}</div>
              </td>
              {/* La société de la personne (migration 102), pas « Membre CSE » pour
                  tout le monde ; le formateur garde sa mention. */}
              <td className="feuille-qualite">
                {p.rang === null
                  ? p.qualite
                  : p.societe || feuille.societeParDefaut || p.qualite || 'Membre CSE'}
              </td>
              {jours.flatMap((j) =>
                DEMI_JOURNEES.map((d) => {
                  const signee = signature(p.id, j, d.cle);
                  return (
                    <td
                      key={`${jourCle(j)}-${d.cle}`}
                      className="feuille-case"
                      data-label={`${jourCourt(j)} ${d.libelle}`}
                    >
                      {signee ? (
                        <span className="feuille-signee">
                          <img src={signee.image} alt={`Signature de ${signee.signataire_nom}`} />
                          <span className="feuille-signee-info">
                            {signee.signataire_nom} · {formatDateTime(signee.signe_le)}
                          </span>
                          <button
                            type="button"
                            className="feuille-effacer"
                            onClick={() => onEffacer(p.id, j, d.cle)}
                            aria-label={`Retirer la signature de ${signee.signataire_nom}`}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="feuille-a-signer"
                          onClick={() => onSigner(p, j, d.cle)}
                        >
                          Signer
                        </button>
                      )}
                    </td>
                  );
                })
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default FeuillesPresence;
